import { useEffect, useRef, useState } from "react";
import { ArrowDownLeft, ArrowUpRight, X } from "lucide-react";
import { getUserFacingErrorMessage } from "@/api/userFacingError";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { FloatingToast } from "@/components/feedback/FloatingToast";
import { useCreateManualCaixaTransaction } from "@/features/caixas/hooks";
import type { ManualCaixaTransactionPayload } from "@/features/caixas/types";
import { useDialogFocus } from "@/hooks/useDialogFocus";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useFloatingFeedbackToast } from "./useFloatingFeedbackToast";

type CaixaSupplier = {
  id: string;
  name: string;
  box_balance: number;
};

interface CaixasManualTransactionModalProps {
  supplier: CaixaSupplier;
  onClose: () => void;
  mutationOverride?: {
    mutateAsync: (args: {
      id: string;
      payload: ManualCaixaTransactionPayload;
    }) => Promise<unknown>;
    isPending: boolean;
  };
}

export function CaixasManualTransactionModal({
  supplier,
  onClose,
  mutationOverride,
}: CaixasManualTransactionModalProps) {
  const dialogTitleId = "supplier-box-transaction-modal-title";
  const hookMutation = useCreateManualCaixaTransaction();
  const mutation = mutationOverride ?? hookMutation;
  const [operation, setOperation] =
    useState<ManualCaixaTransactionPayload["operation"]>("receive");
  const [quantity, setQuantity] = useState<string>("");
  const [observation, setObservation] = useState("");
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const { toast, showToast, dismissToast } = useFloatingFeedbackToast();
  const receiveOperationRef = useRef<HTMLButtonElement | null>(null);
  const sendOperationRef = useRef<HTMLButtonElement | null>(null);
  const dialogRef = useRef<HTMLDivElement | null>(null);

  useDialogFocus(dialogRef, onClose);

  const parsedQuantity = Number(quantity);
  const isValidQuantity =
    Number.isInteger(parsedQuantity) && parsedQuantity > 0;

  async function handleSubmit() {
    if (!isValidQuantity) return;
    try {
      await mutation.mutateAsync({
        id: supplier.id,
        payload: {
          operation,
          quantity: parsedQuantity,
          observation: observation.trim() || undefined,
        },
      });
      onClose();
    } catch (err) {
      showToast({
        tone: "error",
        title: "Não foi possível registrar",
        message: getUserFacingErrorMessage(
          err,
          "Não foi possível atualizar o caixa. Tente novamente.",
        ),
      });
    }
  }

  function handleOperationKeyDown(
    event: React.KeyboardEvent<HTMLButtonElement>,
  ) {
    const isArrowKey = [
      "ArrowRight",
      "ArrowDown",
      "ArrowLeft",
      "ArrowUp",
    ].includes(event.key);
    const nextOperation = isArrowKey
      ? operation === "receive"
        ? "send"
        : "receive"
      : event.key === "Home"
        ? "receive"
        : event.key === "End"
          ? "send"
          : null;

    if (!nextOperation) return;
    event.preventDefault();
    setOperation(nextOperation);
    (nextOperation === "receive"
      ? receiveOperationRef.current
      : sendOperationRef.current
    )?.focus();
  }

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (
        e.key === "Enter" &&
        !e.defaultPrevented &&
        !mutation.isPending &&
        isValidQuantity &&
        !e.shiftKey &&
        !(e.target instanceof HTMLSelectElement) &&
        !(e.target instanceof HTMLButtonElement)
      ) {
        if (e.target instanceof HTMLTextAreaElement) e.preventDefault();
        handleSubmit();
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  });

  const balance = supplier.box_balance ?? 0;
  const balanceColor =
    balance > 0 ? "#16a34a" : balance < 0 ? "#ef4444" : "var(--login-muted)";

  return (
    <>
      <style>{`
        @keyframes supplierBoxOverlayIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes supplierBoxPanelIn {
          from { opacity: 0; transform: translateY(18px) scale(0.985); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        .supplier-box-overlay {
          animation: supplierBoxOverlayIn 0.18s ease both;
        }
        .supplier-box-panel {
          animation: supplierBoxPanelIn 0.24s cubic-bezier(0.16,1,0.3,1) both;
        }
        .supplier-box-choice {
          transition: transform 0.16s ease, border-color 0.16s ease, background 0.16s ease, color 0.16s ease;
        }
        @media (hover: hover) {
          .supplier-box-choice:hover,
          .supplier-box-footer-btn:hover:not(:disabled),
          .supplier-box-close:hover {
            transform: translateY(-1px);
          }
          .supplier-box-close:hover {
            background: color-mix(in srgb, var(--login-accent) 8%, transparent) !important;
            border-color: color-mix(in srgb, var(--login-accent) 36%, var(--login-border)) !important;
            color: var(--login-accent) !important;
          }
        }
        .supplier-box-field {
          color: var(--login-text) !important;
          font-family: 'DM Sans', sans-serif;
          font-size: 0.95rem;
          font-weight: 500;
        }
        .supplier-box-field::placeholder {
          color: var(--login-muted);
          opacity: 1;
          font-weight: 500;
        }
        .supplier-box-footer-btn {
          min-height: 2.75rem;
          border-radius: 4px;
          font-family: 'Barlow Condensed', sans-serif;
          font-weight: 700;
          font-size: 0.95rem;
          letter-spacing: 0.05em;
          transition: transform 0.16s ease, opacity 0.16s ease, background 0.16s ease, border-color 0.16s ease;
        }
        .supplier-box-close {
          transition: transform 0.16s ease, background 0.16s ease, border-color 0.16s ease, color 0.16s ease;
        }
        @media (max-width: 720px) {
          .supplier-box-overlay {
            align-items: flex-end !important;
            padding: 0 !important;
          }
          .supplier-box-panel {
            width: 100% !important;
            max-height: min(92dvh, 620px) !important;
            border-radius: 8px 8px 0 0 !important;
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .supplier-box-overlay,
          .supplier-box-panel,
          .supplier-box-choice,
          .supplier-box-footer-btn,
          .supplier-box-close {
            animation: none !important;
            transition-duration: 0.01ms !important;
            transform: none !important;
          }
        }
      `}</style>
      <div
        className="supplier-box-overlay"
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 220,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "1rem",
          background: "rgba(0,0,0,0.46)",
        }}
      >
        <div
          ref={dialogRef}
          className="supplier-box-panel"
          data-visual-treatment="operational-sheet"
          role="dialog"
          aria-modal="true"
          aria-labelledby={dialogTitleId}
          tabIndex={-1}
          onClick={(e) => e.stopPropagation()}
          style={{
            width: "min(100%, 520px)",
            maxHeight: "calc(100dvh - 2rem)",
            overflow: "auto",
            borderRadius: "6px",
            border: "1px solid var(--login-border)",
            background: "var(--login-surface)",
            boxShadow: "0 18px 48px rgba(15, 23, 42, 0.16)",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: "0.75rem",
              padding: "1rem",
              borderBottom: "1px solid var(--login-border)",
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontFamily: "'Barlow Condensed', sans-serif",
                  fontWeight: 700,
                  fontSize: "0.78rem",
                  letterSpacing: "0.08em",
                  color: "var(--login-muted)",
                  textTransform: "uppercase",
                }}
              >
                Caixa avulsa
              </div>
              <h2
                id={dialogTitleId}
                style={{
                  margin: "0.25rem 0 0",
                  fontFamily: "'Barlow Condensed', sans-serif",
                  fontWeight: 700,
                  fontSize: "1.4rem",
                  letterSpacing: "0.03em",
                  color: "var(--login-text)",
                }}
              >
                {supplier.name}
              </h2>
            </div>
            <button
              onClick={onClose}
              aria-label="Fechar modal de caixa avulsa"
              className="supplier-box-close"
              style={{
                width: "2.75rem",
                height: "2.75rem",
                borderRadius: "4px",
                border: "1px solid var(--login-border)",
                background: "transparent",
                color: "var(--login-text)",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
              }}
            >
              <X size={16} />
            </button>
          </div>

          <div
            style={{
              display: "grid",
              gap: "1rem",
              padding: "1rem",
            }}
          >
            <div
              style={{
                display: "grid",
                gap: "0.3rem",
                padding: "0.15rem 0 0.15rem 0.8rem",
                borderLeft: `3px solid ${balanceColor}`,
              }}
            >
              <span
                style={{
                  fontFamily: "'Barlow Condensed', sans-serif",
                  fontWeight: 700,
                  fontSize: "0.76rem",
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color: "var(--login-muted)",
                }}
              >
                Saldo atual
              </span>
              <span
                style={{
                  fontFamily: "'Barlow Condensed', sans-serif",
                  fontWeight: 700,
                  fontSize: "1.2rem",
                  letterSpacing: "0.03em",
                  color: balanceColor,
                }}
              >
                {balance > 0 ? `+${balance}` : balance} cx
              </span>
            </div>

            <div style={{ display: "grid", gap: "0.6rem" }}>
              <div
                role="radiogroup"
                aria-label="Tipo de movimentação"
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                  gap: 0,
                  overflow: "hidden",
                  border: "1px solid var(--login-border)",
                  borderRadius: "4px",
                }}
              >
                <button
                  ref={receiveOperationRef}
                  type="button"
                  role="radio"
                  aria-checked={operation === "receive"}
                  className="supplier-box-choice"
                  onClick={() => setOperation("receive")}
                  onKeyDown={handleOperationKeyDown}
                  tabIndex={operation === "receive" ? 0 : -1}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "0.5rem",
                    minHeight: "2.75rem",
                    border: "none",
                    borderRight: "1px solid var(--login-border)",
                    background:
                      operation === "receive"
                        ? "rgba(22,163,74,0.09)"
                        : "transparent",
                    color:
                      operation === "receive" ? "#16a34a" : "var(--login-text)",
                    fontFamily: "'Barlow Condensed', sans-serif",
                    fontWeight: 700,
                    fontSize: "0.92rem",
                    letterSpacing: "0.06em",
                    cursor: "pointer",
                  }}
                >
                  <ArrowDownLeft size={16} />
                  Receber
                </button>
                <button
                  ref={sendOperationRef}
                  type="button"
                  role="radio"
                  aria-checked={operation === "send"}
                  className="supplier-box-choice"
                  onClick={() => setOperation("send")}
                  onKeyDown={handleOperationKeyDown}
                  tabIndex={operation === "send" ? 0 : -1}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "0.5rem",
                    minHeight: "2.75rem",
                    border: "none",
                    background:
                      operation === "send"
                        ? "rgba(239,68,68,0.09)"
                        : "transparent",
                    color:
                      operation === "send" ? "#ef4444" : "var(--login-text)",
                    fontFamily: "'Barlow Condensed', sans-serif",
                    fontWeight: 700,
                    fontSize: "0.92rem",
                    letterSpacing: "0.06em",
                    cursor: "pointer",
                  }}
                >
                  <ArrowUpRight size={16} />
                  Enviar
                </button>
              </div>
            </div>

            <Input
              type="number"
              min={1}
              aria-label="Quantidade"
              placeholder="Quantidade"
              value={quantity}
              onInput={(e) => setQuantity(e.currentTarget.value)}
              className="supplier-box-field h-11 bg-transparent"
              style={{ borderRadius: "4px" }}
            />

            <Textarea
              aria-label="Observação"
              placeholder="Observação"
              value={observation}
              onInput={(e) => setObservation(e.currentTarget.value)}
              className="supplier-box-field min-h-28 bg-transparent"
              style={{ borderRadius: "4px" }}
            />

            <div
              style={{
                display: "grid",
                gap: "0.7rem",
                gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              }}
            >
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                className="supplier-box-footer-btn h-11"
                style={{
                  color: "var(--login-text)",
                  borderColor: "var(--login-border)",
                  background: "var(--login-surface)",
                }}
              >
                Cancelar
              </Button>
              <Button
                type="button"
                onClick={handleSubmit}
                disabled={!isValidQuantity || mutation.isPending}
                className="supplier-box-footer-btn h-11"
                style={{
                  background: "var(--login-accent)",
                  color: "#fff",
                  opacity: !isValidQuantity || mutation.isPending ? 0.58 : 1,
                }}
              >
                {mutation.isPending ? "Salvando..." : "Registrar"}
              </Button>
            </div>
          </div>
        </div>
      </div>
      {toast ? (
        <FloatingToast
          title={toast.title}
          message={toast.message}
          tone={toast.tone}
          state={toast.state}
          isDesktop={isDesktop}
          onDismiss={dismissToast}
        />
      ) : null}
    </>
  );
}
